// API client for Textborne backend

import { z } from 'zod';
import {
  User, 
  World, 
  WorldSeed, 
  CreateWorldRequest, 
  AuthResponse, 
  AdminStats,
  AdminUserSummary,
  AdminUserDetail,
  AdminInvitationCode,
  AdminWorldSummary,
  AdminWorldDetail,
  AdminUsageSummary,
  AdminInferenceLog,
  AdminInferenceLogDetail,
  WorldScaffoldingResult,
  ContentRating,
  ScaffoldedCharacter,
  ScaffoldProgressEvent,
  Message,
  CompanionMemory,
  CompanionContact,
  CompanionProfile,
} from '../types';
import { AdminWorldDetailSchema } from '../schemas';

const API_BASE = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

function newRequestId(): string {
  // Client-side crypto.randomUUID is available everywhere modern. Prefix
  // makes logs grep-friendly and distinguishes client-originated IDs from
  // server-generated ones (which use `req_` too, by design — same-ID
  // tracing across the HTTP boundary is the point).
  const uuid = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `req_${uuid}`;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'X-Request-ID': newRequestId(),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed', error.code);
  }
  return response.json();
}

async function handleResponseWithSchema<T>(response: Response, schema: z.ZodSchema<T>): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new ApiError(response.status, error.error || 'Request failed', error.code);
  }
  const data = await response.json();
  const parsed = schema.safeParse(data);
  if (!parsed.success) {
    throw new ApiError(response.status, 'Invalid response from server');
  }
  return parsed.data;
}

// ============================================
// Authentication
// ============================================

export async function register(
  email: string,
  password: string,
  displayName?: string,
): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, displayName }),
  });
  const data = await handleResponse<AuthResponse>(response);
  localStorage.setItem('token', data.token);
  return data;
}

export async function login(email: string, password: string): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  const data = await handleResponse<AuthResponse>(response);
  localStorage.setItem('token', data.token);
  return data;
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE}/auth/logout`, {
      method: 'POST',
      headers: getAuthHeaders(),
    });
  } finally {
    localStorage.removeItem('token');
  }
}

export async function getCurrentUser(): Promise<User | null> {
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const response = await fetch(`${API_BASE}/auth/me`, {
      headers: getAuthHeaders(),
    });
    if (!response.ok) {
      localStorage.removeItem('token');
      return null;
    }
    const data = await response.json();
    return data.user;
  } catch {
    localStorage.removeItem('token');
    return null;
  }
}

export interface UpdateCurrentUserPayload {
  display_name?: string | null;
  handle?: string | null;
  avatar_url?: string | null;
  bio?: string | null;
  timezone?: string | null;
  preferences?: Record<string, unknown> | null;
  email?: string;
}

export async function updateCurrentUser(payload: UpdateCurrentUserPayload): Promise<User> {
  const response = await fetch(`${API_BASE}/auth/me`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  const data = await handleResponse<{ user: User }>(response);
  return data.user;
}

export async function resendVerificationEmail(): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/auth/resend-verification`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ============================================
// Worlds
// ============================================

export async function getWorlds(): Promise<World[]> {
  const response = await fetch(`${API_BASE}/api/worlds`, {
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<{ worlds: World[] }>(response);
  return data.worlds;
}

export async function createWorld(request: CreateWorldRequest): Promise<{ world: World; seed: WorldSeed | null }> {
  const response = await fetch(`${API_BASE}/api/worlds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse(response);
}

/** Current chapter info returned from getWorld */
export interface CurrentChapterInfo {
  chapter_number: number;
  title: string | null;
  primary_location: string | null;
}

export async function getWorld(worldId: string): Promise<{ world: World; seed: WorldSeed | null; currentChapter: CurrentChapterInfo | null; usageStats: { totalCostMicros: number; totalTokens: number } }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function createWsTicket(worldId: string): Promise<{ ticket: string; expires_at: string }> {
  const response = await fetch(`${API_BASE}/api/ws-ticket`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ worldId }),
  });
  return handleResponse(response);
}

// ============================================
// Billing & Credits
// ============================================

export interface BillingStatus {
  customer: { stripe_customer_id: string } | null;
  subscription: {
    stripe_subscription_id: string;
    stripe_price_id: string | null;
    status: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  } | null;
}

export async function getBillingStatus(): Promise<BillingStatus> {
  const response = await fetch(`${API_BASE}/api/billing/status`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export interface CreditOptions {
  min_cents: number;
  max_cents: number;
  presets_cents: number[];
}

export async function getCreditOptions(): Promise<CreditOptions> {
  const response = await fetch(`${API_BASE}/api/billing/credit-options`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export interface PurchaseStatus {
  status: 'pending' | 'complete';
  credits_micros: number | null;
  balance_micros: number | null;
}

/**
 * Has this checkout been fulfilled yet? Stripe redirects the buyer back before
 * its webhook lands, so the success screen polls this rather than assuming.
 */
export async function getPurchaseStatus(sessionId: string): Promise<PurchaseStatus> {
  const response = await fetch(
    `${API_BASE}/api/billing/purchase?session_id=${encodeURIComponent(sessionId)}`,
    { headers: getAuthHeaders() }
  );
  return handleResponse(response);
}

export async function createCheckoutSession(payload: {
  amount_cents?: number;
  success_url?: string;
  cancel_url?: string;
  mode?: 'payment' | 'subscription';
}): Promise<{ session_id: string; url: string | null }> {
  const response = await fetch(`${API_BASE}/api/billing/stripe/checkout-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function createBillingPortalSession(payload?: { return_url?: string }): Promise<{ session_id: string; url: string | null }> {
  const response = await fetch(`${API_BASE}/api/billing/stripe/portal-session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload || {}),
  });
  return handleResponse(response);
}

export interface CreditsBalance {
  balance_micros: number;
  last_reconciled_at: string | null;
}

export async function getCreditsBalance(): Promise<CreditsBalance> {
  const response = await fetch(`${API_BASE}/api/credits/balance`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export interface CreditLedgerEntry {
  id: string;
  user_id: string;
  world_id: string | null;
  api_usage_id: string | null;
  delta_micros: number;
  reason: string;
  note: string | null;
  metadata_json: string | null;
  created_at: string;
}

export async function getCreditsLedger(limit = 50): Promise<{ entries: CreditLedgerEntry[] }> {
  const response = await fetch(`${API_BASE}/api/credits/ledger?limit=${limit}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ============================================
// World Creation Wizard APIs
// ============================================

export interface ScaffoldWorldRequest {
  title: string;
  prompt: string;
  genre?: string;
  tone?: string;
  themes?: string[];
  contentRating: ContentRating;
}



/**
 * Scaffold world with rich SSE progress streaming
 * Uses parallel architecture: Story Shaping + Character Listing in parallel (~15s)
 * 
 * Uses POST with streaming response (not EventSource) to:
 * 1. Avoid URL length limits for large prompts
 * 2. Use proper auth headers
 * 3. Send data in request body
 */
export async function scaffoldWorld(
  request: ScaffoldWorldRequest,
  onProgress: (event: ScaffoldProgressEvent) => void
): Promise<WorldScaffoldingResult> {
  const response = await fetch(`${API_BASE}/api/worlds/scaffold`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      title: request.title,
      prompt: request.prompt,
      genre: request.genre,
      tone: request.tone,
      themes: request.themes,
      contentRating: request.contentRating || 'standard',
    }),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Scaffolding request failed' }));
    throw new Error(error.error || 'Scaffolding request failed');
  }
  
  if (!response.body) {
    throw new Error('No response body');
  }
  
  // Read SSE stream from POST response
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result: WorldScaffoldingResult | null = null;
  let streamError: Error | null = null;
  
  // Helper to process SSE events from text
  const processEvents = (text: string, isFlush = false) => {
    buffer += text;
    
    // SSE events are separated by double newlines
    const events = buffer.split('\n\n');
    
    // Keep the last incomplete chunk in buffer (unless flushing)
    if (!isFlush) {
      buffer = events.pop() || '';
    } else {
      buffer = '';
    }
    
    for (const eventText of events) {
      if (!eventText.trim()) continue;
      
      const lines = eventText.split('\n');
      let eventType = '';
      let eventData = '';
      
      for (const line of lines) {
        if (line.startsWith('event: ')) {
          eventType = line.slice(7);
        } else if (line.startsWith('data: ')) {
          eventData = line.slice(6);
        }
      }
      
      if (eventType && eventData) {
        try {
          const data = JSON.parse(eventData);
          
          if (eventType === 'progress') {
            onProgress(data as ScaffoldProgressEvent);
          } else if (eventType === 'complete') {
            result = data.result as WorldScaffoldingResult;
            // Attach traceId from the complete event
            if (data.traceId && result) {
              result.scaffoldingTraceId = data.traceId;
            }
            // Attach agentUsage for cost tracking
            if (data.usage?.agentUsage && result) {
              result.scaffoldingAgentUsage = data.usage.agentUsage;
            }
          } else if (eventType === 'error') {
            streamError = new Error(data.message || 'Scaffolding failed');
            return;
          }
        } catch (e) {
          console.warn('Failed to parse SSE event:', eventType, eventData, e);
        }
      }
    }
  };
  
  while (true) {
    const { done, value } = await reader.read();
    
    if (done) {
      // Flush any remaining buffer
      if (buffer.trim()) {
        processEvents('', true);
      }
      break;
    }
    
    processEvents(decoder.decode(value, { stream: true }));
    if (streamError) throw streamError;
  }

  if (streamError) throw streamError;
  
  if (!result) {
    throw new Error('Scaffolding completed without result');
  }
  
  return result;
}

// ============================================
// Guided Curation API
// ============================================

export type CurationStep = 'foundation' | 'setting' | 'character' | 'npc_stubs' | 'conflict' | 'opening';

export interface CurationRequest {
  step: CurationStep;
  draft: Record<string, unknown>;
  userInput: Record<string, unknown>;
}

export interface CurationResponse {
  step: CurationStep;
  result: Record<string, unknown>;
  usage: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
  };
  latencyMs: number;
}

/**
 * Call a curation agent to expand user input for a guided world-building step.
 * Each step takes prior context (draft) and user input, returns curated output.
 */
export async function curateWorldStep(request: CurationRequest): Promise<CurationResponse> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 120000); // 2 minute timeout per call
  
  try {
    const response = await fetch(`${API_BASE}/api/worlds/curate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders(),
      },
      body: JSON.stringify(request),
      signal: controller.signal,
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Curation failed' }));
      throw new Error(error.error || 'Curation failed');
    }
    
    return response.json();
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') {
      throw new Error(`Curation timed out after 2 minutes (step: ${request.step})`);
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Find characters the reviewed cast is missing and enrich them.
 * Extracts from the concept (prompt mode) and/or the setting (guided mode).
 * `failedNames` lists characters that were found but could not be enriched;
 * they are not included in `characters`.
 */
export async function reextractCharacters(request: {
  prompt?: string;
  settingDefinition?: string;
  contentRating: 'standard' | 'mature' | 'explicit';
  existingCharacterNames: string[];
  genre?: string;
  tone?: string;
  themes?: string[];
}): Promise<{ characters: ScaffoldedCharacter[]; failedNames: string[] }> {
  const response = await fetch(`${API_BASE}/api/worlds/reextract-characters`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Re-extraction failed' }));
    throw new Error(error.error || 'Re-extraction failed');
  }
  
  return response.json();
}

/**
 * Enhance a character using AI based on a user description
 * Returns enhanced field values for review
 * @param request.worldPrompt - Original world concept (for context)
 * @param request.settingDefinition - Scaffolded setting (preferred context)
 * @param request.guidedContext - Rich context from guided world creation
 */
export async function enhanceCharacter(request: {
  description: string;
  worldPrompt?: string;
  settingDefinition?: string;
  existingData?: Partial<ScaffoldedCharacter>;
  guidedContext?: {
    // User's RAW input - most important!
    originalCharacterConcept?: string;
    originalRole?: string;
    // World context
    genre?: string;
    tone?: string;
    themes?: string[];
    worldRules?: string[];
    keyLocations?: Array<{ name: string; description: string }>;
    conflict?: {
      centralConflict?: string;
      stakes?: string;
    };
    // Opening scene user input
    openingScene?: string;
    startingLocation?: string;
    // Other characters
    otherCharacters?: Array<{ name: string; roleInStory?: string; primaryDrive?: string }>;
    relationships?: Array<{ fromCharacter: string; toCharacter: string; surfaceRelationship: string }>;
  };
}): Promise<Partial<ScaffoldedCharacter>> {
  const response = await fetch(`${API_BASE}/api/characters/enhance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Enhancement failed' }));
    throw new Error(error.error || 'Enhancement failed');
  }
  
  return response.json();
}

// ============================================
// World Settings
// ============================================

export interface UpdateWorldSettingsRequest {
  title?: string;
  provider?: string;
  model?: string;
  content_rating?: 'standard' | 'mature' | 'explicit';
  allow_nsfw?: boolean;
  reasoning_effort?: 'minimal' | 'low' | 'medium' | 'high' | 'max' | null;
}

export async function updateWorldSettings(
  worldId: string,
  settings: UpdateWorldSettingsRequest
): Promise<void> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/settings`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(settings),
  });
  await handleResponse(response);
}

// ============================================
// Messages
// ============================================

/**
 * Fetch all messages for a world (for export/copy)
 */
export async function getAllMessages(worldId: string): Promise<Message[]> {
  const response = await fetch(
    `${API_BASE}/api/worlds/${worldId}/messages`,
    { headers: getAuthHeaders() }
  );
  const data = await handleResponse<{ messages: Message[] }>(response);
  return data.messages;
}

export interface ModelInfo {
  id: string;
  provider: string;
  modelId: string;
  displayName: string;
  categories: string[];
  contextWindow: number;
  pricing: { input: number; output: number };
  supportsReasoning: boolean;
  supportedReasoningEfforts?: Array<'minimal' | 'low' | 'medium' | 'high' | 'max'>;
  defaultReasoningEffort?: 'minimal' | 'low' | 'medium' | 'high' | 'max';
  reasoningMandatory: boolean;
  avgLatencyMs: number;
}

export async function getAvailableModels(): Promise<ModelInfo[]> {
  const response = await fetch(`${API_BASE}/api/models`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<ModelInfo[]>(response);
}

export async function deleteWorld(worldId: string): Promise<void> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await handleResponse(response);
}

// ============================================
// Admin API
// ============================================

export async function getAdminStats(): Promise<AdminStats> {
  const response = await fetch(`${API_BASE}/admin/stats`, {
    headers: getAuthHeaders(),
  });
  return handleResponse<AdminStats>(response);
}

export type AdminUserSortKey =
  | 'email'
  | 'world_count'
  | 'message_count'
  | 'total_tokens'
  | 'total_cost_micros'
  | 'total_billed_micros'
  | 'total_profit_micros'
  | 'balance_micros'
  | 'last_active'
  | 'last_logged_in_at'
  | 'created_at';

export type AdminUserSortDirection = 'asc' | 'desc';

export async function getAdminUsers(
  limit = 50,
  offset = 0,
  search = '',
  sortBy: AdminUserSortKey = 'created_at',
  sortDirection: AdminUserSortDirection = 'desc'
): Promise<{ users: AdminUserSummary[]; total: number }> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
    sort_by: sortBy,
    sort_direction: sortDirection,
  });
  if (search) params.set('search', search);

  const response = await fetch(`${API_BASE}/admin/users?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminUserDetail(userId: string): Promise<AdminUserDetail> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function updateAdminUser(
  userId: string,
  payload: {
    email?: string;
    display_name?: string | null;
    is_admin?: boolean;
    email_verified?: boolean;
  }
): Promise<AdminUserDetail> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function deleteAdminUser(userId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export interface CreditGrantEmailNotification {
  status: 'not_requested' | 'accepted' | 'failed';
  attempts: number;
  provider_message_id?: string;
  provider_status_code?: number;
  error?: string;
}

export interface AdminCreditGrantResult {
  balance_micros: number;
  ledger_id: string;
  email_notification: CreditGrantEmailNotification;
}

export async function adminAddCredits(
  userId: string,
  amountDollars: number,
  note?: string,
  notifyUser = amountDollars > 0
): Promise<AdminCreditGrantResult> {
  const response = await fetch(`${API_BASE}/admin/users/${userId}/credits`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ amount_dollars: amountDollars, note, notify_user: notifyUser }),
  });
  return handleResponse(response);
}

export async function retryAdminCreditGrantEmail(
  userId: string,
  ledgerId: string
): Promise<{ ledger_id: string; email_notification: CreditGrantEmailNotification }> {
  const response = await fetch(
    `${API_BASE}/admin/users/${userId}/credits/${ledgerId}/retry-email`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    }
  );
  return handleResponse(response);
}

export async function getAdminWorlds(
  limit = 50,
  offset = 0,
  userId?: string,
  includeDeleted = false
): Promise<{ worlds: AdminWorldSummary[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (userId) params.set('userId', userId);
  if (includeDeleted) params.set('includeDeleted', 'true');

  const response = await fetch(`${API_BASE}/admin/worlds?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminWorldDetail(worldId: string): Promise<AdminWorldDetail> {
  const response = await fetch(`${API_BASE}/admin/worlds/${worldId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponseWithSchema(response, AdminWorldDetailSchema);
}

export async function getAdminUsage(days = 30): Promise<{ usage: AdminUsageSummary[] }> {
  const response = await fetch(`${API_BASE}/admin/usage?days=${days}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export interface AdminModelStat {
  provider: string;
  model: string;
  displayName: string | null;
  contentTier: 'sfw' | 'moderated' | 'permissive' | 'unrestricted' | null;
  contentCaveats: string[] | null;
  qualityTiers: string[];
  sampleCount: number;
  successCount: number;
  successRate: number;
  avgLatencyMs: number | null;
  maxLatencyMs: number | null;
  totalPromptTokens: number;
  totalCompletionTokens: number;
  totalTokens: number;
  totalCostMicros: number;
  costPerKTokensMicros: number | null;
  avgTokensPerRequest: number | null;
  avgCostPerRequestMicros: number | null;
  streamedCount: number;
  streamedFraction: number;
  lastSeenAt: string;
}

export interface AdminModelStatsResponse {
  windowHours: number;
  generatedAt: string;
  totalRequests: number;
  totalCostMicros: number;
  totalTokens: number;
  modelCount: number;
  stats: AdminModelStat[];
}

export async function getAdminModelStats(windowHours = 24): Promise<AdminModelStatsResponse> {
  const response = await fetch(`${API_BASE}/admin/model-stats?windowHours=${windowHours}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ===== Turn quality signals =====

export interface AdminSignalRate {
  answered: number;
  positive: number;
}

export interface AdminTurnSignalSummary {
  kind: 'narrative' | 'companion';
  servedModel: string | null;
  turns: number;
  fallbackShare: number;
  rates: Record<string, AdminSignalRate>;
  meanNpcAgency: number | null;
  npcAgencyAnswered: number;
  corrections: AdminSignalRate;
  costPerAcceptedTurnMicros: number | null;
}

export interface AdminTurnSignalsResponse {
  windowHours: number;
  generatedAt: string;
  models: AdminTurnSignalSummary[];
}

export async function getAdminTurnSignals(windowHours = 24): Promise<AdminTurnSignalsResponse> {
  const response = await fetch(`${API_BASE}/admin/turn-signals?windowHours=${windowHours}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ===== Retrieval backfill (T3) =====

export interface AdminReindexResponse {
  worldId: string;
  messageCount: number;
  indexed: number;
  skipped: number;
  failed: number;
  reasons: Record<string, number>;
  remaining: number;
  totalIndexed: number;
  totalPairs: number;
}

export async function reindexAdminWorld(
  worldId: string,
  limit: number = 40,
): Promise<AdminReindexResponse> {
  const response = await fetch(
    `${API_BASE}/admin/worlds/${encodeURIComponent(worldId)}/reindex?limit=${limit}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    },
  );
  return handleResponse(response);
}

export interface AdminRevoiceResponse {
  worldId: string;
  /** Characters whose voices the pass will rewrite, in order. */
  characters: string[];
}

/** Start rewriting every character's voice; the pass runs on in the world and reports through its inference log. */
export async function revoiceAdminWorld(worldId: string): Promise<AdminRevoiceResponse> {
  const response = await fetch(`${API_BASE}/admin/worlds/${encodeURIComponent(worldId)}/revoice`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ===== Eval harness =====

export interface AdminEvalFixture {
  id: string;
  description: string;
  queryCount: number;
}

export interface AdminEvalRunResponse {
  report: unknown;
  markdown: string;
}

export async function getAdminEvalFixtures(): Promise<{ fixtures: AdminEvalFixture[] }> {
  const response = await fetch(`${API_BASE}/admin/eval/fixtures`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function runAdminEvalFixture(
  fixtureId: string,
  opts?: { label?: string; maxQueries?: number; temperature?: number },
): Promise<AdminEvalRunResponse> {
  const params = new URLSearchParams();
  if (opts?.label) params.set('label', opts.label);
  if (opts?.maxQueries != null) params.set('maxQueries', String(opts.maxQueries));
  if (opts?.temperature != null) params.set('temperature', String(opts.temperature));
  const qs = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_BASE}/admin/eval/run/${encodeURIComponent(fixtureId)}${qs}`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminInferenceRequestTypes(): Promise<{ requestTypes: string[] }> {
  const response = await fetch(`${API_BASE}/admin/inference/request-types`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminInferenceLogs(
  limit = 50,
  offset = 0,
  filters?: {
    worldId?: string;
    userId?: string;
    requestType?: string;
    agentName?: string;
    search?: string;
    successOnly?: boolean;
    errorsOnly?: boolean;
  }
): Promise<{ logs: AdminInferenceLog[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  if (filters?.worldId) params.set('worldId', filters.worldId);
  if (filters?.userId) params.set('userId', filters.userId);
  if (filters?.requestType) params.set('requestType', filters.requestType);
  if (filters?.agentName) params.set('agentName', filters.agentName);
  if (filters?.search) params.set('search', filters.search);
  if (filters?.successOnly) params.set('successOnly', 'true');
  if (filters?.errorsOnly) params.set('errorsOnly', 'true');

  const response = await fetch(`${API_BASE}/admin/inference?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminInferenceLogDetail(logId: string): Promise<AdminInferenceLogDetail> {
  const response = await fetch(`${API_BASE}/admin/inference/${logId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ============================================
// Admin Invitation Codes API
// ============================================

export async function getAdminInvitationCodes(): Promise<{ codes: AdminInvitationCode[] }> {
  const response = await fetch(`${API_BASE}/admin/invitation-codes`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function createAdminInvitationCode(payload: {
  maxUses?: number | null;
  note?: string;
  expiresAt?: string;
}): Promise<{ code: AdminInvitationCode }> {
  const response = await fetch(`${API_BASE}/admin/invitation-codes`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function updateAdminInvitationCode(
  id: string,
  payload: {
    maxUses?: number | null;
    note?: string;
    expiresAt?: string | null;
  }
): Promise<{ code: AdminInvitationCode }> {
  const response = await fetch(`${API_BASE}/admin/invitation-codes/${id}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function deleteAdminInvitationCode(id: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/admin/invitation-codes/${id}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ============================================
// Admin Tracing API
// ============================================

export interface TraceSummary {
  id: string;
  worldId: string;
  userId: string;
  triggerType: 'user_message' | 'opening_narrative' | 'curation' | 'sculpt' | 'world_creation';
  triggerContent: string | null;
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalLatencyMs: number;
  rootSpanId: string | null;
  status: 'in_progress' | 'completed' | 'failed';
  errorMessage: string | null;
  startedAt: string;
  completedAt: string | null;
  spans?: TraceSpan[];
}

export interface TraceSpan {
  spanId: string;
  parentSpanId: string | null;
  agentName: string;
  depth: number;
  model: string;
  provider: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
  success: boolean;
  errorCode?: string;
  startedAt: string;
  /** System prompt used for this call */
  systemPrompt?: string;
  /** JSON array of input messages */
  messagesJson?: string;
  /** Final response content */
  responseContent?: string;
  /** Phase group for trace organization: scaffolding, preflight, processing, postprocessing */
  phaseGroup?: 'scaffolding' | 'preflight' | 'processing' | 'postprocessing';
}

export async function getAdminWorldTraces(
  worldId: string,
  limit = 50,
  offset = 0
): Promise<{ traces: TraceSummary[]; total: number }> {
  const params = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const response = await fetch(`${API_BASE}/admin/worlds/${worldId}/traces?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminTracesByTriggerType(
  triggerType: TraceSummary['triggerType'],
  limit = 50,
  offset = 0
): Promise<{ traces: TraceSummary[]; total: number }> {
  const params = new URLSearchParams({ 
    triggerType, 
    limit: String(limit), 
    offset: String(offset) 
  });
  const response = await fetch(`${API_BASE}/admin/traces?${params}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getAdminTrace(traceId: string): Promise<TraceSummary> {
  const response = await fetch(`${API_BASE}/admin/traces/${traceId}`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

// ============================================
// World branching
// ============================================

export interface BranchWorldResponse {
  worldId: string;
  /** The input branched at, for the player to send again or change. */
  prefill: string | null;
}

/** Start a new world from this story as it stood at a message. */
export async function branchWorld(worldId: string, messageId: string): Promise<BranchWorldResponse> {
  const response = await fetch(`${API_BASE}/api/worlds/${encodeURIComponent(worldId)}/branch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ messageId }),
  });
  return handleResponse(response);
}

export interface AdminBranchResponse {
  worldId: string;
  turn: number;
  messageCount: number;
}

/** Copy any world, as it stands now, into the operator's account. */
export async function branchAdminWorld(worldId: string): Promise<AdminBranchResponse> {
  const response = await fetch(`${API_BASE}/admin/worlds/${encodeURIComponent(worldId)}/branch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: '{}',
  });
  return handleResponse(response);
}

// ============================================
// Companion APIs
// ============================================

// ============================================
// Import APIs
// ============================================

export interface ParsedConversation {
  provider: 'claude' | 'chatgpt' | 'gemini' | 'manual';
  title: string | null;
  messages: Array<{ role: 'user' | 'assistant'; content: string }>;
  tokenEstimate: number;
  fallbackRequired: boolean;
}

export interface ImportSummaryResult {
  title: string;
  worldConcept: string;
  genre: string;
  tone: string;
}

export async function parseShareLink(url: string): Promise<ParsedConversation> {
  const response = await fetch(`${API_BASE}/api/import/parse`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ url }),
  });
  return handleResponse(response);
}

export async function parseConversationText(text: string): Promise<ParsedConversation> {
  const response = await fetch(`${API_BASE}/api/import/parse-text`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ text }),
  });
  return handleResponse(response);
}

export async function summarizeForImport(
  messages: Array<{ role: 'user' | 'assistant'; content: string }>
): Promise<ImportSummaryResult> {
  const response = await fetch(`${API_BASE}/api/import/summarize`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ messages }),
  });
  return handleResponse(response);
}

export interface ImportCompanionRequest {
  title: string;
  companionName?: string;
  userPreferredName?: string;
  role?: 'friend' | 'mentor' | 'coach' | 'confidant' | 'partner' | 'custom';
  communicationMedium?: 'text_message' | 'chat_app' | 'social_dm' | 'voice_call' | 'video_call' | 'in_person' | 'letters' | 'journal';
  boundaries?: {
    allowRoleplay?: boolean;
    allowRomance?: boolean;
    safetyMode?: 'standard' | 'permissive';
    topicsToAvoid?: string[];
  };
  importedMessages: Array<{ role: 'user' | 'assistant'; content: string }>;
  sourceProvider: string;
}

export async function importCompanionWorld(request: ImportCompanionRequest): Promise<{ world: World }> {
  const response = await fetch(`${API_BASE}/api/import/companion`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(request),
  });
  return handleResponse(response);
}

// ============================================
// Companion APIs
// ============================================

export async function getCompanionMemories(worldId: string): Promise<{ memories: CompanionMemory[] }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/memories`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function createCompanionMemory(
  worldId: string,
  payload: {
    type: CompanionMemory['type'];
    key: string;
    value: string;
    confidence?: number;
    isUserConfirmed?: boolean;
  }
): Promise<{ memory: CompanionMemory }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/memories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function updateCompanionMemory(
  worldId: string,
  memoryId: string,
  payload: Partial<{
    type: CompanionMemory['type'];
    key: string;
    value: string;
    confidence: number;
    isUserConfirmed: boolean;
  }>
): Promise<{ memory: CompanionMemory | null }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/memories/${memoryId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function deleteCompanionMemory(worldId: string, memoryId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/memories/${memoryId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getCompanionContacts(worldId: string): Promise<{ contacts: CompanionContact[] }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/contacts`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function createCompanionContact(
  worldId: string,
  payload: {
    name: string;
    relation?: string | null;
    notes?: string | null;
    sentiment?: string | null;
  }
): Promise<{ contact: CompanionContact }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/contacts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function updateCompanionContact(
  worldId: string,
  contactId: string,
  payload: {
    name?: string;
    relation?: string | null;
    notes?: string | null;
    sentiment?: string | null;
  }
): Promise<{ contact: CompanionContact | null }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/contacts/${contactId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}

export async function deleteCompanionContact(worldId: string, contactId: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/contacts/${contactId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function getCompanionProfile(worldId: string): Promise<{ profile: CompanionProfile | null }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/companion/profile`, {
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
}

export async function updateCompanionProfile(
  worldId: string,
  payload: {
    name?: string;
    userPreferredName?: string | null;
    pronouns?: string | null;
    apparentAge?: string | null;
    userDescription?: string | null;
    role?: CompanionProfile['role'] | null;
    relationshipStance?: string | null;
    communicationMedium?: 'text_message' | 'chat_app' | 'social_dm' | 'voice_call' | 'video_call' | 'in_person' | 'letters' | 'journal' | null;
    speechPatterns?: string | null;
    vocabularyTier?: string | null;
    emotionalBaseline?: string | null;
    primaryDrive?: string | null;
    attachmentStyle?: string | null;
    communicationStyle?: string | null;
    coreWound?: string | null;
    defenseMechanisms?: string[] | null;
    blindSpots?: string[] | null;
    hiddenDesires?: string[] | null;
    fears?: string[] | null;
    contradictions?: string[] | null;
    formativeExperiences?: string[] | null;
    personalValues?: string[] | null;
    quirks?: string[] | null;
    innerWorld?: string | null;
    backstory?: string | null;
    currentLifeSituation?: string | null;
    occupation?: string | null;
    interests?: string[] | null;
    acknowledgeAi?: boolean | null;
    boundaries?: {
      allowRoleplay?: boolean;
      allowRomance?: boolean;
      topicsToAvoid?: string[];
      safetyMode?: 'standard' | 'permissive';
    };
    provider?: string | null;
    model?: string | null;
  }
): Promise<{ profile: CompanionProfile | null }> {
  const response = await fetch(`${API_BASE}/api/worlds/${worldId}/companion/profile`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
}
